import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Scissors, LogOut, LayoutDashboard, CalendarCheck, DollarSign, Users, TrendingUp, MessageSquare, Star, Check, Trash2, Clock, Plus, Pencil, Tag, Image as ImageIcon, Upload, QrCode, Download, Video, Menu, X } from "lucide-react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ShareButtons, { SITE_URL } from "@/components/ShareButtons";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = ["confirmed", "completed", "cancelled", "no_show"];
const FILTERS = ["all", ...STATUSES];
const EMPTY_SERVICE = { name: "", description: "", price: "", duration: "", category: "", featured: false };

const NAV_ITEMS = [
  { key: "appointments", label: "Appointments", icon: LayoutDashboard, testid: "nav-appointments" },
  { key: "reviews", label: "Reviews", icon: MessageSquare, testid: "nav-reviews", badge: true },
  { key: "services", label: "Services", icon: Tag, testid: "nav-services" },
  { key: "gallery", label: "Gallery", icon: ImageIcon, testid: "nav-gallery" },
  { key: "testimonials", label: "Testimonials", icon: Video, testid: "nav-testimonials" },
  { key: "share", label: "Share & QR", icon: QrCode, testid: "nav-share" },
];

function NavLinks({ view, goto, pendingCount }) {
  return (
    <div className="flex flex-col gap-1 flex-1">
      {NAV_ITEMS.map((n) => {
        const active = view === n.key;
        const Icon = n.icon;
        return (
          <button
            key={n.key}
            onClick={() => goto(n.key)}
            className={`flex items-center justify-between px-4 py-3 overline text-xs transition-colors ${active ? "bg-[#d4af37]/10 border-l-2 border-[#d4af37] text-white" : "text-white/50 hover:text-white border-l-2 border-transparent"}`}
            data-testid={n.testid}
          >
            <span className="flex items-center gap-3"><Icon className="w-4 h-4" strokeWidth={1.5} /> {n.label}</span>
            {n.badge && pendingCount > 0 && (
              <span className="bg-[#d4af37] text-black text-[0.6rem] font-bold px-2 py-0.5 rounded-full" data-testid="pending-badge">{pendingCount}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("appointments");
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Switch view + close the mobile drawer on tap
  const goto = (v) => { setView(v); setMobileNavOpen(false); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, rv, sv, gl, tm] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/appointments${filter !== "all" ? `?status=${filter}` : ""}`),
        api.get("/admin/reviews"),
        api.get("/admin/services"),
        api.get("/gallery"),
        api.get("/testimonials"),
      ]);
      setStats(s.data);
      setAppointments(a.data);
      setReviews(rv.data);
      setServices(sv.data);
      setGallery(gl.data);
      setTestimonials(tm.data);
      setPendingCount(rv.data.filter((r) => !r.approved).length);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      toast.success(`Marked as ${status.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const approveReview = async (id, response) => {
    try {
      await api.patch(`/admin/reviews/${id}`, response !== undefined ? { response } : { approved: true });
      toast.success(response !== undefined ? "Reply saved" : "Review approved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveService = async (payload, id) => {
    try {
      if (id) await api.patch(`/admin/services/${id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(id ? "Service updated" : "Service added");
      load();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    }
  };

  const deleteService = async (id) => {
    try {
      await api.delete(`/admin/services/${id}`);
      toast.success("Service deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const syncCatalog = async () => {
    try {
      const { data } = await api.post("/admin/services/sync-catalog");
      toast.success(data.message || "Catalog synced");
      load();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    }
  };

  const uploadGalleryImage = async (fileObj, caption) => {
    const fd = new FormData();
    fd.append("file", fileObj);
    fd.append("caption", caption || "");
    try {
      await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      load();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    }
  };

  const deleteGalleryImage = async (id) => {
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Photo removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadTestimonial = async (fileObj, clientName, caption) => {
    const fd = new FormData();
    fd.append("file", fileObj);
    fd.append("client_name", clientName || "");
    fd.append("caption", caption || "");
    try {
      await api.post("/admin/testimonials", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video uploaded");
      load();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Video removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const statCards = stats ? [
    { icon: CalendarCheck, label: "Today", value: stats.today },
    { icon: Users, label: "Confirmed", value: stats.confirmed },
    { icon: TrendingUp, label: "Completed", value: stats.completed },
    { icon: DollarSign, label: "Revenue", value: `$${stats.revenue}` },
    { icon: LayoutDashboard, label: "Total", value: stats.total },
  ] : [];

  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="admin-dashboard">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 h-14 border-b border-white/10 bg-[#0a0a0a]/95 backdrop-blur">
        <div className="flex items-center gap-2">
          <Scissors className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} />
          <span className="font-display text-white text-sm">The Barbers <span className="italic text-[#d4af37]">Chair</span></span>
        </div>
        <button
          onClick={() => setMobileNavOpen(true)}
          className="p-2 border border-white/15 text-white/80 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
          aria-label="Open menu"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Mobile drawer overlay */}
      {mobileNavOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-backdrop"
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`lg:hidden fixed top-0 left-0 z-50 h-screen w-72 border-r border-white/10 bg-[#0a0a0a] p-6 transition-transform duration-300 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="mobile-nav-drawer"
      >
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <Scissors className="w-5 h-5 text-[#d4af37]" strokeWidth={1.5} />
            <span className="font-display text-white text-sm">The Barbers <span className="italic text-[#d4af37]">Chair</span></span>
          </div>
          <button
            onClick={() => setMobileNavOpen(false)}
            className="p-1.5 text-white/60 hover:text-white transition-colors"
            aria-label="Close menu"
            data-testid="mobile-menu-close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <NavLinks view={view} goto={goto} pendingCount={pendingCount} />
        <div className="border-t border-white/10 pt-6 mt-8">
          <p className="text-white/70 font-body text-sm">{user?.name}</p>
          <p className="text-white/30 font-body text-xs mb-4">{user?.email}</p>
          <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-2 overline text-xs text-white/50 hover:text-white transition-colors" data-testid="mobile-admin-logout">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-white/10 bg-[#0a0a0a] fixed h-screen p-8">
        <div className="flex items-center gap-3 mb-16">
          <Scissors className="w-5 h-5 text-[#d4af37]" strokeWidth={1.5} />
          <span className="font-display text-white">The Barbers <span className="italic text-[#d4af37]">Chair</span></span>
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          <NavLinks view={view} goto={goto} pendingCount={pendingCount} />
        </nav>
        <div className="border-t border-white/10 pt-6">
          <p className="text-white/70 font-body text-sm">{user?.name}</p>
          <p className="text-white/30 font-body text-xs mb-4">{user?.email}</p>
          <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-2 overline text-xs text-white/50 hover:text-white transition-colors" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:ml-64 p-6 lg:p-12 pt-28 lg:pt-12">
        <div className="max-w-6xl">
          <p className="overline text-xs text-[#d4af37] mb-4">Owner Dashboard</p>
          <h1 className="font-display text-4xl lg:text-5xl text-white mb-12">{view === "reviews" ? "Reviews" : view === "services" ? "Services" : view === "gallery" ? "Gallery" : view === "testimonials" ? "Video Testimonials" : view === "share" ? "Share & QR" : "Appointments"}</h1>

          {view === "appointments" && (
          <>
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-14">
            {statCards.map((c, i) => (
              <motion.div key={c.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="border border-white/10 p-6 bg-[#0a0a0a]" data-testid={`stat-${c.label.toLowerCase()}`}>
                <c.icon className="w-5 h-5 text-[#d4af37] mb-4" strokeWidth={1.5} />
                <p className="font-display text-3xl text-white">{c.value}</p>
                <p className="overline text-[0.55rem] text-white/40 mt-1">{c.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`px-5 py-2 overline text-[0.6rem] border transition-colors ${filter === f ? "bg-[#d4af37] border-[#d4af37] text-black" : "border-white/15 text-white/60 hover:border-[#d4af37] hover:text-[#d4af37]"}`} data-testid={`filter-${f}`}>
                {f.replace("_", " ")}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="border border-white/10 bg-[#0a0a0a] overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  {["Client", "Service", "Date", "Time", "Price", "Payment", "Status"].map((h) => (
                    <th key={h} className="overline text-[0.55rem] text-white/40 px-6 py-4 font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="px-6 py-10 text-white/40 font-body animate-pulse">Loading…</td></tr>}
                {!loading && appointments.length === 0 && <tr><td colSpan={7} className="px-6 py-10 text-white/40 font-body">No appointments found.</td></tr>}
                {appointments.map((a) => (
                  <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`admin-appt-${a.id}`}>
                    <td className="px-6 py-5">
                      <p className="font-body text-white text-sm">{a.customer_name}</p>
                      <p className="font-body text-white/40 text-xs">{a.customer_phone}</p>
                    </td>
                    <td className="px-6 py-5">
                      <p className="font-body text-white/70 text-sm">{a.service_name}</p>
                      {a.location && (
                        <p className="overline text-[0.55rem] text-[#d4af37]/80 mt-1 tracking-widest" data-testid={`appt-loc-${a.id}`}>
                          {a.location === "wilson" ? "Wilson" : a.location === "clayton" ? "Clayton" : a.location}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-5 font-body text-white/70 text-sm">{new Date(a.date + "T00:00:00").toLocaleDateString()}</td>
                    <td className="px-6 py-5 font-body text-white/70 text-sm">{a.time_slot}</td>
                    <td className="px-6 py-5 font-display text-white">${a.service_price}</td>
                    <td className="px-6 py-5">
                      <PaymentBadge status={a.payment_status} />
                    </td>
                    <td className="px-6 py-5">
                      <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                        <SelectTrigger className="w-[150px] bg-transparent border-white/15 text-white text-xs" data-testid={`status-select-${a.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-white/15 text-white">
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} className="text-white text-xs focus:bg-[#d4af37]/20 focus:text-white capitalize">{s.replace("_", " ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
          )}

          {view === "reviews" && (
            <ReviewsPanel reviews={reviews} loading={loading} onApprove={approveReview} onDelete={deleteReview} />
          )}

          {view === "services" && (
            <ServicesPanel services={services} loading={loading} onSave={saveService} onDelete={deleteService} onSyncCatalog={syncCatalog} />
          )}

          {view === "gallery" && (
            <GalleryPanel gallery={gallery} loading={loading} onUpload={uploadGalleryImage} onDelete={deleteGalleryImage} />
          )}

          {view === "testimonials" && (
            <TestimonialsPanel videos={testimonials} loading={loading} onUpload={uploadTestimonial} onDelete={deleteTestimonial} />
          )}

          {view === "share" && <SharePanel />}
        </div>
      </main>
    </div>
  );
}

function ReviewsPanel({ reviews, loading, onApprove, onDelete }) {
  const pending = reviews.filter((r) => !r.approved);
  const approved = reviews.filter((r) => r.approved);
  return (
    <div className="space-y-14" data-testid="reviews-panel">
      <div>
        <p className="overline text-xs text-[#d4af37] mb-5 flex items-center gap-2"><Clock className="w-4 h-4" /> Pending Approval ({pending.length})</p>
        {loading && <p className="text-white/40 font-body animate-pulse">Loading…</p>}
        {!loading && pending.length === 0 && <p className="text-white/40 font-body text-sm">No reviews awaiting approval.</p>}
        <div className="space-y-4">
          {pending.map((r) => (
            <ReviewCard key={r.id} r={r} onApprove={onApprove} onDelete={onDelete} pending />
          ))}
        </div>
      </div>

      <div>
        <p className="overline text-xs text-white/50 mb-5">Published ({approved.length})</p>
        <div className="space-y-4">
          {approved.map((r) => (
            <ReviewCard key={r.id} r={r} onApprove={onApprove} onDelete={onDelete} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ReviewCard({ r, onApprove, onDelete, pending }) {
  const [reply, setReply] = useState(r.response || "");
  return (
    <div className={`border p-6 bg-[#0a0a0a] ${pending ? "border-[#d4af37]/40" : "border-white/10"}`} data-testid={`admin-review-${r.id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-3">
            <p className="font-body text-white text-sm font-semibold">{r.author}</p>
            <div className="flex gap-0.5">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-3 h-3 fill-[#d4af37] text-[#d4af37]" />)}</div>
          </div>
          <p className="overline text-[0.55rem] text-[#d4af37]/70 mt-1">{r.service || "General"} · {r.date}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {pending && (
            <button onClick={() => onApprove(r.id)} className="inline-flex items-center gap-1 bg-[#d4af37] text-black px-4 py-2 overline text-[0.6rem] hover:bg-[#c5a028] transition-colors" data-testid={`approve-review-${r.id}`}>
              <Check className="w-3.5 h-3.5" /> Approve
            </button>
          )}
          <button onClick={() => onDelete(r.id)} className="inline-flex items-center gap-1 border border-white/15 text-white/60 px-3 py-2 overline text-[0.6rem] hover:border-red-400 hover:text-red-400 transition-colors" data-testid={`delete-review-${r.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <p className="text-white/70 text-sm leading-relaxed font-body mb-4">{r.text}</p>
      <div className="flex items-center gap-2">
        <input
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Write a reply…"
          className="flex-1 bg-transparent border-b border-white/15 py-2 text-white text-sm font-body focus:border-[#d4af37] focus:outline-none transition-colors"
          data-testid={`reply-input-${r.id}`}
        />
        <button onClick={() => onApprove(r.id, reply)} className="overline text-[0.6rem] text-[#d4af37] hover:text-white transition-colors px-3 py-2" data-testid={`save-reply-${r.id}`}>Save Reply</button>
      </div>
    </div>
  );
}

function ServicesPanel({ services, loading, onSave, onDelete, onSyncCatalog }) {
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [seedList, setSeedList] = useState([]);

  // Load the current code-level catalog whenever the dialog opens, so the warning
  // text always reflects today's seed — not stale copy from months ago.
  useEffect(() => {
    if (!syncOpen) return;
    api.get("/admin/services/seed").then((r) => setSeedList(r.data || [])).catch(() => setSeedList([]));
  }, [syncOpen]);

  const visible = services.filter((s) => !s.hidden);
  const hidden = services.filter((s) => s.hidden);
  const grouped = {};
  visible.forEach((s) => { (grouped[s.category] = grouped[s.category] || []).push(s); });

  return (
    <div className="space-y-10" data-testid="services-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="overline text-xs text-white/50">
          {visible.length} active {hidden.length > 0 && <span className="text-white/30">· {hidden.length} hidden</span>}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSyncOpen(true)}
            className="inline-flex items-center gap-2 border border-[#d4af37] text-[#d4af37] px-5 py-2.5 overline text-xs hover:bg-[#d4af37] hover:text-black transition-colors"
            data-testid="sync-catalog-btn"
          >
            Reset to Current Catalog
          </button>
          <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 bg-[#d4af37] text-black px-6 py-2.5 overline text-xs hover:bg-[#c5a028] transition-colors" data-testid="add-service-btn">
            <Plus className="w-4 h-4" /> Add Service
          </button>
        </div>
      </div>

      {loading && <p className="text-white/40 font-body animate-pulse">Loading…</p>}

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <p className="overline text-xs text-[#d4af37] mb-4">{cat}</p>
          <div className="border border-white/10 bg-[#0a0a0a] divide-y divide-white/5">
            {items.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-4 px-6 py-4" data-testid={`service-row-${s.id}`}>
                <div className="min-w-0">
                  <p className="font-body text-white text-sm flex items-center gap-2">
                    {s.name}
                    {s.featured && <Star className="w-3 h-3 fill-[#d4af37] text-[#d4af37]" />}
                  </p>
                  <p className="overline text-[0.55rem] text-white/40 mt-1">{s.duration} min · ${s.price}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setEditing(s)} className="border border-white/15 text-white/60 p-2 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors" data-testid={`edit-service-${s.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setDeleteTarget(s)} className="border border-white/15 text-white/60 p-2 hover:border-red-400 hover:text-red-400 transition-colors" data-testid={`delete-service-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {hidden.length > 0 && (
        <div data-testid="hidden-services-section">
          <p className="overline text-xs text-white/30 mb-4">Hidden ({hidden.length}) — not shown on the booking page</p>
          <div className="border border-white/5 bg-[#050505] divide-y divide-white/5 opacity-60">
            {hidden.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-4 px-6 py-3" data-testid={`hidden-service-row-${s.id}`}>
                <div className="min-w-0">
                  <p className="font-body text-white/60 text-sm">{s.name}</p>
                  <p className="overline text-[0.55rem] text-white/30 mt-1">{s.category} · ${s.price}</p>
                </div>
                <button onClick={() => setDeleteTarget(s)} className="border border-white/10 text-white/40 p-2 hover:border-red-400 hover:text-red-400 transition-colors" data-testid={`delete-hidden-service-${s.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <ServiceForm
        service={editing}
        open={editing !== undefined}
        onClose={() => setEditing(undefined)}
        onSave={async (payload, id) => { const ok = await onSave(payload, id); if (ok) setEditing(undefined); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/15 text-white" data-testid="delete-service-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-white">Delete service?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50 font-body">
              {deleteTarget && `"${deleteTarget.name}" will be removed from the booking menu. Existing appointments are not affected.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={() => { onDelete(deleteTarget.id); setDeleteTarget(null); }} className="bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-service">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={syncOpen} onOpenChange={(o) => !syncing && setSyncOpen(o)}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/15 text-white" data-testid="sync-catalog-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-white">
              Reset to your {seedList.length || ""}-service catalog?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 font-body space-y-2">
              <span className="block">This will keep only your current catalog active on the public booking page{seedList.length > 0 ? ":" : "."}</span>
              {seedList.length > 0 && (
                <span className="block text-white/80" data-testid="sync-seed-list">
                  {seedList.map((s) => s.name).join(" · ")}
                </span>
              )}
              <span className="block text-white/40 text-xs">All other services will be hidden (not deleted). Existing appointments and history are preserved.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={syncing} className="bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={syncing}
              onClick={async (e) => {
                e.preventDefault();
                setSyncing(true);
                const ok = await onSyncCatalog();
                setSyncing(false);
                if (ok) setSyncOpen(false);
              }}
              className="bg-[#d4af37] text-black hover:bg-[#c5a028]"
              data-testid="confirm-sync-catalog"
            >
              {syncing ? "Syncing…" : "Reset Catalog"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ServiceForm({ service, open, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_SERVICE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (service) setForm({ ...EMPTY_SERVICE, ...service, price: String(service.price), duration: String(service.duration) });
    else setForm(EMPTY_SERVICE);
  }, [service, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.category || form.price === "" || form.duration === "") {
      toast.error("Name, category, price and duration are required.");
      return;
    }
    setSaving(true);
    await onSave({
      name: form.name.trim(),
      description: form.description || "",
      price: parseFloat(form.price),
      duration: parseInt(form.duration, 10),
      category: form.category.trim(),
      featured: !!form.featured,
    }, service?.id);
    setSaving(false);
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 py-2.5 text-white font-body focus:border-[#d4af37] focus:outline-none transition-colors";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-white/15 text-white max-w-lg" data-testid="service-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white">{service ? "Edit Service" : "New Service"}</DialogTitle>
          <DialogDescription className="text-white/50 font-body">Manage the details customers see when booking this service.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-1">Name *</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="service-name-input" />
          </div>
          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-1">Category *</label>
            <input value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Haircuts" className={inputCls} data-testid="service-category-input" />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="overline text-[0.6rem] text-white/50 block mb-1">Price ($) *</label>
              <input type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} data-testid="service-price-input" />
            </div>
            <div>
              <label className="overline text-[0.6rem] text-white/50 block mb-1">Duration (min) *</label>
              <input type="number" min="0" value={form.duration} onChange={(e) => set("duration", e.target.value)} className={inputCls} data-testid="service-duration-input" />
            </div>
          </div>
          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-1">Description</label>
            <input value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="service-description-input" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="service-featured-toggle">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#d4af37] w-4 h-4" />
            <span className="font-body text-white/70 text-sm">Featured (highlighted on the site)</span>
          </label>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="border border-white/20 px-6 py-2.5 overline text-xs text-white/70 hover:text-white transition-colors" data-testid="service-form-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="bg-[#d4af37] text-black px-8 py-2.5 overline text-xs disabled:opacity-40 hover:bg-[#c5a028] transition-colors" data-testid="service-form-save">
            {saving ? "Saving…" : (service ? "Save Changes" : "Add Service")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GalleryPanel({ gallery, loading, onUpload, onDelete }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const submit = async () => {
    if (!file) { toast.error("Choose an image first."); return; }
    setUploading(true);
    const ok = await onUpload(file, caption);
    setUploading(false);
    if (ok) { setFile(null); setCaption(""); }
  };

  return (
    <div className="space-y-10" data-testid="gallery-panel">
      <div className="border border-white/10 bg-[#0a0a0a] p-6">
        <p className="overline text-xs text-[#d4af37] mb-5 flex items-center gap-2"><Upload className="w-4 h-4" /> Add a Photo</p>
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-2">Image (JPG/PNG/WebP)</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-white/70 font-body file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#d4af37] file:text-black file:overline file:text-[0.6rem] file:cursor-pointer"
              data-testid="gallery-file-input"
            />
          </div>
          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-2">Caption (optional)</label>
            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Skin fade + beard"
              className="w-full bg-transparent border-b border-white/20 py-2 text-white font-body focus:border-[#d4af37] focus:outline-none transition-colors"
              data-testid="gallery-caption-input"
            />
          </div>
          <button onClick={submit} disabled={uploading} className="bg-[#d4af37] text-black px-6 py-2.5 overline text-xs disabled:opacity-40 hover:bg-[#c5a028] transition-colors" data-testid="gallery-upload-btn">
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>

      {loading && <p className="text-white/40 font-body animate-pulse">Loading…</p>}
      {!loading && gallery.length === 0 && <p className="text-white/40 font-body text-sm">No photos yet. Upload fresh cuts to show them on your homepage.</p>}

      <div className="columns-2 lg:columns-3 gap-4 [&>*]:mb-4">
        {gallery.map((g) => (
          <div key={g.id} className="relative group border border-white/10 break-inside-avoid overflow-hidden" data-testid={`gallery-item-${g.id}`}>
            <img src={`${backend}${g.url}`} alt={g.caption || "Gallery"} className="w-full h-auto object-cover" loading="lazy" />
            {g.caption && <p className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent overline text-[0.55rem] text-[#d4af37]">{g.caption}</p>}
            <button onClick={() => setDeleteTarget(g)} className="absolute top-2 right-2 bg-black/70 border border-white/20 text-white/70 p-2 opacity-0 group-hover:opacity-100 hover:border-red-400 hover:text-red-400 transition-all" data-testid={`delete-gallery-${g.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/15 text-white" data-testid="delete-gallery-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-white">Remove this photo?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50 font-body">It will no longer appear in your homepage gallery.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={() => { onDelete(deleteTarget.id); setDeleteTarget(null); }} className="bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-gallery">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SharePanel() {
  const [target, setTarget] = useState("home");
  const url = target === "book" ? `${SITE_URL}/book` : SITE_URL;

  const download = () => {
    const canvas = document.getElementById("tbc-qr-canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `barbers-chair-qr-${target}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR code downloaded");
  };

  return (
    <div className="max-w-3xl space-y-10" data-testid="share-panel">
      <div className="grid md:grid-cols-2 gap-8 items-start">
        {/* QR */}
        <div className="border border-white/10 bg-[#0a0a0a] p-8 text-center">
          <p className="overline text-xs text-[#d4af37] mb-6">Your QR Code</p>
          <div className="bg-white p-5 inline-block" data-testid="qr-wrapper">
            <QRCodeCanvas
              id="tbc-qr-canvas"
              value={url}
              size={1024}
              level="H"
              includeMargin
              fgColor="#0d0d0d"
              bgColor="#ffffff"
              style={{ width: 220, height: 220 }}
            />
          </div>
          <p className="font-body text-white/40 text-xs mt-5 break-all">{url}</p>
          <button
            onClick={download}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#d4af37] text-black py-3 overline text-xs hover:bg-[#c5a028] transition-colors"
            data-testid="download-qr-btn"
          >
            <Download className="w-4 h-4" /> Download PNG (print-ready)
          </button>
          <p className="text-white/30 text-[0.65rem] font-body mt-3">High-resolution (1024px) — perfect for business cards, flyers & window decals.</p>
        </div>

        {/* Controls + share */}
        <div className="space-y-8">
          <div className="border border-white/10 bg-[#0a0a0a] p-6">
            <p className="overline text-xs text-[#d4af37] mb-4">Where should it point?</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setTarget("home")}
                className={`py-4 border overline text-[0.6rem] transition-colors ${target === "home" ? "bg-[#d4af37] border-[#d4af37] text-black" : "border-white/15 text-white/60 hover:border-[#d4af37]"}`}
                data-testid="qr-target-home"
              >
                Homepage
              </button>
              <button
                onClick={() => setTarget("book")}
                className={`py-4 border overline text-[0.6rem] transition-colors ${target === "book" ? "bg-[#d4af37] border-[#d4af37] text-black" : "border-white/15 text-white/60 hover:border-[#d4af37]"}`}
                data-testid="qr-target-book"
              >
                Book Now Page
              </button>
            </div>
            <p className="text-white/40 text-xs font-body mt-4">Tip: use the <b className="text-white/70">Book Now</b> QR on cards so clients land straight on the booking flow.</p>
          </div>

          <div className="border border-white/10 bg-[#0a0a0a] p-6">
            <ShareButtons url={url} text="Book your next cut at The Barbers Chair 💈" label="Share your page online" />
          </div>
        </div>
      </div>
    </div>
  );
}

function TestimonialsPanel({ videos, loading, onUpload, onDelete }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [file, setFile] = useState(null);
  const [clientName, setClientName] = useState("");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const submit = async () => {
    if (!file) { toast.error("Choose a video first."); return; }
    setUploading(true);
    const ok = await onUpload(file, clientName, caption);
    setUploading(false);
    if (ok) { setFile(null); setClientName(""); setCaption(""); }
  };

  return (
    <div className="space-y-10" data-testid="testimonials-panel">
      <div className="border border-white/10 bg-[#0a0a0a] p-6">
        <p className="overline text-xs text-[#d4af37] mb-2 flex items-center gap-2"><Upload className="w-4 h-4" /> Add a Video Testimonial</p>
        <p className="text-white/40 font-body text-xs mb-5">Short vertical clips work best (MP4/MOV, under 60 MB). They autoplay muted on your homepage.</p>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div className="sm:col-span-2">
            <label className="overline text-[0.6rem] text-white/50 block mb-2">Video file</label>
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm,.m4v"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-white/70 font-body file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#d4af37] file:text-black file:overline file:text-[0.6rem] file:cursor-pointer"
              data-testid="testimonial-file-input"
            />
          </div>
          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-2">Client name (optional)</label>
            <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="e.g. Marcus D." className="w-full bg-transparent border-b border-white/20 py-2 text-white font-body focus:border-[#d4af37] focus:outline-none transition-colors" data-testid="testimonial-name-input" />
          </div>
          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-2">Caption (optional)</label>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Skin fade regular" className="w-full bg-transparent border-b border-white/20 py-2 text-white font-body focus:border-[#d4af37] focus:outline-none transition-colors" data-testid="testimonial-caption-input" />
          </div>
        </div>
        <button onClick={submit} disabled={uploading} className="bg-[#d4af37] text-black px-6 py-2.5 overline text-xs disabled:opacity-40 hover:bg-[#c5a028] transition-colors" data-testid="testimonial-upload-btn">
          {uploading ? "Uploading…" : "Upload Video"}
        </button>
      </div>

      {loading && <p className="text-white/40 font-body animate-pulse">Loading…</p>}
      {!loading && videos.length === 0 && <p className="text-white/40 font-body text-sm">No videos yet. Upload client clips to show them on your homepage reel.</p>}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {videos.map((v) => (
          <div key={v.id} className="relative group border border-white/10 overflow-hidden bg-black aspect-[9/16]" data-testid={`testimonial-item-${v.id}`}>
            <video src={`${backend}${v.url}`} className="w-full h-full object-cover" muted playsInline preload="metadata" controls />
            {(v.client_name || v.caption) && (
              <div className="absolute top-0 left-0 right-0 p-2 bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
                {v.client_name && <p className="font-body text-white text-xs">{v.client_name}</p>}
                {v.caption && <p className="overline text-[0.5rem] text-[#d4af37]">{v.caption}</p>}
              </div>
            )}
            <button onClick={() => setDeleteTarget(v)} className="absolute bottom-2 right-2 bg-black/70 border border-white/20 text-white/70 p-2 opacity-0 group-hover:opacity-100 hover:border-red-400 hover:text-red-400 transition-all" data-testid={`delete-testimonial-${v.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/15 text-white" data-testid="delete-testimonial-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-white">Remove this video?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50 font-body">It will no longer appear in your homepage testimonials reel.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={() => { onDelete(deleteTarget.id); setDeleteTarget(null); }} className="bg-red-500 text-white hover:bg-red-600" data-testid="confirm-delete-testimonial">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PaymentBadge({ status }) {
  const map = {
    paid: "text-emerald-400 border-emerald-400/40",
    pending: "text-[#d4af37] border-[#d4af37]/40",
    pay_at_shop: "text-white/50 border-white/20",
    expired: "text-red-400 border-red-400/40",
    failed: "text-red-400 border-red-400/40",
  };
  const labels = { paid: "Paid", pending: "Pending", pay_at_shop: "Pay at shop", expired: "Expired", failed: "Failed" };
  const s = status || "pay_at_shop";
  return <span className={`overline text-[0.55rem] border px-3 py-1.5 ${map[s] || "text-white/50 border-white/20"}`}>{labels[s] || s}</span>;
}
